/**
 * The member's stored view of the tours home (grid size or list). Kept free of
 * UI imports: the root route loader and the skeletons read it, so it must stay
 * light. `view-mode-toggle.tsx` and `tour-home.tsx` use the same keys and
 * storage helpers from here.
 */
export type ViewMode = 'list' | 'gallery';
export type GallerySize = 'small' | 'medium' | 'large';

export const TOURS_VIEW_KEYS = { mode: 'urbania360.tours.view', size: 'urbania360.tours.size' } as const;

export function readStored(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStored(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable: the choice just lasts for the session */
  }
}

/** Reads a stored mode / size with the defaults `usePersistedView` applies (`defaultMode` gallery, size medium). */
export function parseStoredView(
  keys: { mode: string; size: string },
  defaultMode: ViewMode = 'gallery',
): { mode: ViewMode; size: GallerySize } {
  const mode = readStored(keys.mode);
  const size = readStored(keys.size);
  return {
    mode: mode === 'gallery' || mode === 'list' ? mode : defaultMode,
    size: size === 'small' || size === 'large' ? size : 'medium',
  };
}

/** The tours home view as stored, or the default (medium gallery). */
export function readStoredTourView(): { mode: ViewMode; size: GallerySize } {
  return parseStoredView(TOURS_VIEW_KEYS);
}
